import React from 'react';
import {
  format,
  parseISO,
} from 'date-fns';
import {
  Clock,
  MapPin,
  IndianRupee,
  CalendarDays,
  Pencil,
  Trash2,
  Camera,
  AlertCircle,
  Users,
  Star,
  Tag,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { cn } from '../../lib/utils';
import { EVENT_TYPE_CONFIG } from './eventConstants';
import { useDeleteEvent } from '../../hooks/useEvents';
import { toast } from 'sonner';

const TYPE_ICONS = {
  shoot_day: Camera,
  deadline: AlertCircle,
  review: Star,
  meeting: Users,
  custom: Tag,
};

export default function EventDetailSheet({ event, open, onClose, onEdit, isReadOnly }) {
  const deleteMutation = useDeleteEvent();

  if (!event) return null;

  const cfg = EVENT_TYPE_CONFIG[event.event_type] || EVENT_TYPE_CONFIG.custom;
  const TypeIcon = TYPE_ICONS[event.event_type] || Tag;
  const startDate = typeof event.start_at === 'string' ? parseISO(event.start_at) : new Date(event.start_at);
  const endDate = typeof event.end_at === 'string' ? parseISO(event.end_at) : new Date(event.end_at);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(event.id);
      toast.success('Event deleted');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={cn('flex items-center justify-center rounded-lg h-8 w-8', cfg.light)}>
              <TypeIcon className="h-4 w-4" />
            </div>
            <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider', cfg.light)}>
              {cfg.label}
            </Badge>
          </div>
          <SheetTitle className="text-xl font-display">{event.title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-6">
          {/* Description */}
          {event.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p>
          )}

          {/* Date & time */}
          <div className="flex items-start gap-3">
            <CalendarDays className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">{format(startDate, 'EEEE, MMMM d, yyyy')}</p>
              {event.all_day ? (
                <p className="text-muted-foreground">All Day</p>
              ) : (
                <p className="text-muted-foreground">
                  {format(startDate, 'h:mm a')} – {format(endDate, 'h:mm a')}
                </p>
              )}
            </div>
          </div>

          {/* Location */}
          {event.location_text && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm">{event.location_text}</p>
            </div>
          )}

          {/* Cost */}
          {event.cost_amount != null && Number(event.cost_amount) > 0 && (
            <div className="flex items-start gap-3">
              <IndianRupee className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm font-medium">
                {event.cost_currency || 'INR'}{' '}
                {Number(event.cost_amount).toLocaleString('en-IN')}
              </p>
            </div>
          )}

          {/* Time metadata */}
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>Created {format(parseISO(event.created_at), 'MMM d, yyyy h:mm a')}</p>
              {event.updated_at !== event.created_at && (
                <p>Updated {format(parseISO(event.updated_at), 'MMM d, yyyy h:mm a')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions (film scope only) */}
        {!isReadOnly && (
          <div className="flex items-center gap-2 border-t pt-4">
            <Button onClick={() => onEdit(event)} className="flex-1 gap-2">
              <Pencil className="h-4 w-4" />
              Edit Event
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" className="shrink-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Event</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{event.title}"? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
